import { get, post, postForm, patch, del } from "./client";
import type { FormUploadProgress } from "./client";
import { orderedPlaybackClientLogBody, orderedPlaybackEventBody, playbackSessionOwnerID } from "./playbackEventOrdering";
import type {
  SingleResponse,
  CollectionResponse,
  Session,
  Library,
  Item,
  MediaCollection,
  User,
  APIKey,
  DeviceCodeAuthorization,
  UserSettings,
  Config,
  AcquisitionQualityProfile,
  AcquisitionRootFolder,
  Job,
  PlaybackSession,
  Plugin,
  PluginCatalogEntry,
  PluginNavEntry,
  PluginAdminNavEntry,
  DiscoveryHomeResponse,
  DiscoveryFacet,
  ContentRequest,
  RequestAvailability,
  RequestDiscoveryResult,
  RequestDiscoverySettings,
  RequestResolution,
  SubtitleProviderResult,
  Download,
  Artwork,
  CastMember,
  MetadataMatch,
  MetadataIssuesResponse,
  PlaybackDiagnosticsResponse,
  AdminActivitySystem,
  LoginRequest,
  SessionStatus,
  CreateLibraryRequest,
  CreatePlaybackSessionRequest,
  ScanRequest,
  PaginationParams,
  ItemFilterParams,
  DiscoveryFacetFilterParams,
  MetadataIssueFilterParams,
  SearchParams,
  SystemCapabilities,
  Resource,
  HealthAttributes,
  BootstrapAdminRequest,
  WatchStateResource,
} from "@/types/api";

const API_MAX_PAGE_LIMIT = 100;
const WATCH_STATE_BATCH_SIZE = 75;

/** System */

export function getHealth() {
  return get<SingleResponse<Resource<"system", HealthAttributes>>>(
    "/system/health",
  );
}

export function getSystemInfo() {
  return get<SingleResponse<Resource<"system", SystemCapabilities>>>(
    "/system/info",
  );
}

export function getCapabilities() {
  return get<SingleResponse<Resource<"system", SystemCapabilities>>>(
    "/system/capabilities",
  );
}

/** Auth */

export function login(req: LoginRequest) {
  return post<SingleResponse<Session>>("/auth/login", req);
}

export function createInitialAdmin(req: BootstrapAdminRequest) {
  return post<SingleResponse<Session>>("/setup/admin", req);
}

export function logout() {
  return post<void>("/auth/logout");
}

export function getSession() {
  return get<SingleResponse<SessionStatus>>("/auth/session");
}

export function createAPIKey(body: { name?: string }) {
  return post<SingleResponse<APIKey>>("/auth/api-keys", body);
}

export function getAPIKeys() {
  return get<CollectionResponse<APIKey>>("/auth/api-keys");
}

export function deleteAPIKey(keyId: string) {
  return del(`/auth/api-keys/${keyId}`);
}

export function getDeviceCode(userCode: string) {
  return get<SingleResponse<DeviceCodeAuthorization>>(
    `/auth/device/${encodeURIComponent(userCode)}`,
  );
}

export function approveDeviceCode(userCode: string) {
  return post<SingleResponse<DeviceCodeAuthorization>>(
    `/auth/device/${encodeURIComponent(userCode)}/approve`,
  );
}

export function denyDeviceCode(userCode: string) {
  return post<SingleResponse<DeviceCodeAuthorization>>(
    `/auth/device/${encodeURIComponent(userCode)}/deny`,
  );
}

/** Users */

export function getUsers(params?: PaginationParams) {
  return get<CollectionResponse<User>>("/users", params);
}

export function getMe() {
  return get<SingleResponse<User>>("/users/me");
}

export function getMySettings() {
  return get<SingleResponse<UserSettings>>("/users/me/settings");
}

export function updateMySettings(body: Partial<{
  tmdb_api_key: string;
  library_default_sort: string;
  preferred_audio_language: string;
  preferred_subtitle_language: string;
  playback_diagnostics_enabled: boolean;
  playback_error_reporting_enabled: boolean;
}>) {
  return patch<SingleResponse<UserSettings>>("/users/me/settings", body);
}

export function getConfig() {
  return get<SingleResponse<Config>>("/config");
}

export function updateConfig(body: Partial<{
  server_name: string;
  default_language: string;
  tmdb_api_key: string;
  rotten_tomatoes_key: string;
  external_discovery_url: string;
  radarr_enabled: boolean;
  radarr_url: string;
  radarr_api_key: string;
  radarr_root_folder_path: string;
  radarr_quality_profile_id: number;
  radarr_default_resolution: RequestResolution;
  radarr_max_resolution: RequestResolution;
  radarr_minimum_availability: string;
  sonarr_enabled: boolean;
  sonarr_url: string;
  sonarr_api_key: string;
  sonarr_root_folder_path: string;
  sonarr_quality_profile_id: number;
  sonarr_default_resolution: RequestResolution;
  sonarr_max_resolution: RequestResolution;
  sonarr_language_profile_id: number;
  opensubtitles_enabled: boolean;
  opensubtitles_api_key: string;
  opensubtitles_default_language: string;
}>) {
  return patch<SingleResponse<Config>>("/config", body);
}

export function searchExternalSubtitles(itemId: string, lang?: string) {
  const query: Record<string, string> = {};
  if (lang) query.lang = lang;
  return get<CollectionResponse<SubtitleProviderResult>>(
    `/items/${itemId}/subtitles/search`,
    query,
  );
}

export function downloadExternalSubtitle(
  itemId: string,
  body: { file_id: number; language: string; file_name?: string },
) {
  return post<SingleResponse<Resource<"subtitle_track", { language: string; file_path: string }>>>(
    `/items/${itemId}/subtitles/download`,
    body,
  );
}

export function testAcquisitionConnection(body: { service: "radarr" | "sonarr" }) {
  return post<SingleResponse<Resource<"acquisition_connection", { service: string; ok: boolean }>>>("/config/acquisition/test", body);
}

export type AcquisitionOptionsResponse = CollectionResponse<AcquisitionQualityProfile> & {
  meta: {
    service: "radarr" | "sonarr";
    root_folders: AcquisitionRootFolder[];
  };
};

export function getAcquisitionOptions(service: "radarr" | "sonarr") {
  return get<AcquisitionOptionsResponse>("/config/acquisition/options", { service });
}

export function getUser(userId: string) {
  return get<SingleResponse<User>>(`/users/${userId}`);
}

export function createUser(body: {
  username: string;
  password: string;
  display_name?: string;
  is_admin?: boolean;
  is_enabled?: boolean;
  can_download?: boolean;
  can_request?: boolean;
  all_libraries?: boolean;
  allowed_library_ids?: string[];
}) {
  return post<SingleResponse<User>>("/users", body);
}

export function updateUser(
  userId: string,
  body: Partial<{ display_name: string; is_admin: boolean; is_enabled: boolean; can_download: boolean; can_request: boolean; password: string; all_libraries: boolean; allowed_library_ids: string[] }>,
) {
  return patch<SingleResponse<User>>(`/users/${userId}`, body);
}

export function deleteUser(userId: string) {
  return del(`/users/${userId}`);
}

/** Libraries */

export function getLibraries(params?: PaginationParams) {
  return get<CollectionResponse<Library>>("/libraries", params);
}

export function getLibrary(libraryId: string) {
  return get<SingleResponse<Library>>(`/libraries/${libraryId}`);
}

export function createLibrary(body: CreateLibraryRequest) {
  return post<SingleResponse<Library>>("/libraries", body);
}

export function updateLibrary(
  libraryId: string,
  body: Partial<{
    name: string;
    library_type: Library["attributes"]["library_type"];
    source: {
      kind?: string;
      path?: string;
    };
  }>,
) {
  return patch<SingleResponse<Library>>(`/libraries/${libraryId}`, body);
}

export function deleteLibrary(libraryId: string) {
  return del(`/libraries/${libraryId}`);
}

export function scanLibrary(libraryId: string, body?: ScanRequest) {
  return post<SingleResponse<Job>>(`/libraries/${libraryId}/scan`, body);
}

export function getLibraryItems(
  libraryId: string,
  params?: PaginationParams & ItemFilterParams & { sort?: string },
) {
  return get<CollectionResponse<Item>>(
    `/libraries/${libraryId}/items`,
    params,
  );
}

/** Collections */

export function getCollections(params?: PaginationParams) {
  return get<CollectionResponse<MediaCollection>>("/collections", params);
}

export function getCollection(collectionId: string) {
  return get<SingleResponse<MediaCollection>>(`/collections/${collectionId}`);
}

/** Items */

export function getItems(params?: PaginationParams & ItemFilterParams & { sort?: string }) {
  return get<CollectionResponse<Item>>("/items", params);
}

export async function getAllItems(params?: PaginationParams & ItemFilterParams & { sort?: string }) {
  const merged: Item[] = [];
  let cursor: string | undefined;
  let pageLimit = API_MAX_PAGE_LIMIT;

  do {
    const response = await get<CollectionResponse<Item>>("/items", {
      ...params,
      limit: params?.limit ?? API_MAX_PAGE_LIMIT,
      cursor,
    });
    merged.push(...response.data);
    cursor = response.page.next_cursor ?? undefined;
    pageLimit = response.page.limit;
  } while (cursor);

  return {
    data: merged,
    page: {
      limit: pageLimit,
      next_cursor: null,
    },
    meta: {},
  } satisfies CollectionResponse<Item>;
}

export function getItem(itemId: string) {
  return get<SingleResponse<Item>>(`/items/${itemId}`);
}

export function getItemChildren(itemId: string) {
  return get<CollectionResponse<Item>>(`/items/${itemId}/children`);
}

export function getSimilarItems(itemId: string, params?: PaginationParams) {
  return get<CollectionResponse<Item>>(`/items/${itemId}/similar`, params);
}

export function updateItem(
  itemId: string,
  body: Partial<{
    title: string;
    overview: string;
    year: number;
    release_date: string;
    tmdb_id: string;
    source_path: string;
  }>,
) {
  return patch<SingleResponse<Item>>(`/items/${itemId}`, body);
}

export function searchItemMetadata(itemId: string, params?: { q?: string; year?: number }) {
  return get<CollectionResponse<MetadataMatch>>(`/items/${itemId}/metadata/search`, params);
}

export function refreshItem(itemId: string) {
  return post<SingleResponse<Job>>(`/items/${itemId}/refresh`);
}

export interface BadCopyAttributes {
  media_type: string;
  reason: string;
  resolution_class: string;
  current_resolution: number;
  state: string;
  message: string;
  grabbed_title: string;
  flagged_at: string;
  updated_at: string;
}

export function markItemBad(itemId: string, reason?: string) {
  return post<SingleResponse<Resource<"bad_copy", BadCopyAttributes>>>(
    `/items/${itemId}/mark-bad`,
    reason ? { reason } : {},
  );
}

export function analyzeItemIntroMarkers(itemId: string) {
  return post<SingleResponse<Job>>(`/items/${itemId}/intro-markers/analyze`);
}

export function analyzeItemSkipMarkers(itemId: string) {
  return post<SingleResponse<Job>>(`/items/${itemId}/skip-markers/analyze`);
}

export function backfillIntroMarkers(mode: "missing" | "force") {
  return post<SingleResponse<Job>>(`/admin/intro-markers/backfill?mode=${mode}`);
}

export function getItemArtwork(itemId: string) {
  return get<CollectionResponse<Artwork>>(`/items/${itemId}/artwork`);
}

export function getItemCast(itemId: string) {
  return get<CollectionResponse<CastMember>>(`/items/${itemId}/cast`);
}

export function getItemSubtitles(itemId: string) {
  return get<CollectionResponse<Resource<"subtitle", { language: string; format: string }>>>(
    `/items/${itemId}/subtitles`,
  );
}

export function updateItemWatchState(
  itemId: string,
  watched: boolean,
  options: { preserveSessionId?: string | null } = {},
) {
  const preserveSessionId = options.preserveSessionId?.trim();
  return patch<SingleResponse<PlaybackSession | null>>(
    `/items/${itemId}/watch-state`,
    preserveSessionId
      ? { watched, preserve_session_id: preserveSessionId }
      : { watched },
  );
}

export async function getWatchStates(itemIds: string[]) {
  const ids = Array.from(new Set(itemIds.map((itemId) => itemId.trim()).filter(Boolean)));
  if (ids.length === 0) {
    return {
      data: [],
      page: {
        limit: API_MAX_PAGE_LIMIT,
        next_cursor: null,
      },
      meta: {},
    } satisfies CollectionResponse<WatchStateResource>;
  }

  const merged: WatchStateResource[] = [];
  for (let start = 0; start < ids.length; start += WATCH_STATE_BATCH_SIZE) {
    const batch = ids.slice(start, start + WATCH_STATE_BATCH_SIZE);
    const response = await get<CollectionResponse<WatchStateResource>>("/watch-state", { item_ids: batch.join(",") });
    merged.push(...response.data);
  }

  return {
    data: merged,
    page: {
      limit: API_MAX_PAGE_LIMIT,
      next_cursor: null,
    },
    meta: {},
  } satisfies CollectionResponse<WatchStateResource>;
}

/** Search */

export function search(params: SearchParams) {
  return get<CollectionResponse<Item>>("/search", params);
}

/** Discovery, requests, and downloads */

export function getDiscoveryHome(params?: { limit?: number }) {
  return get<DiscoveryHomeResponse>("/discovery/home", params);
}

export function getDiscoveryFacets(params?: DiscoveryFacetFilterParams) {
  return get<CollectionResponse<DiscoveryFacet>>("/discovery/facets", params);
}

export function getWatchlist() {
  return get<CollectionResponse<Item>>("/users/me/watchlist");
}

export function addToWatchlist(itemId: string) {
  return post<SingleResponse<Resource<"watchlist", { item_count: number }>>>(`/users/me/watchlist/${itemId}`);
}

export function removeFromWatchlist(itemId: string) {
  return del(`/users/me/watchlist/${itemId}`);
}

export function getContentRequests() {
  return get<CollectionResponse<ContentRequest>>("/requests");
}

export function createContentRequest(body: {
  title: string;
  media_type: "movie" | "series";
  external_id?: string;
  source?: string;
  critic_score?: number;
  audience_score?: number;
  note?: string;
}) {
	return post<SingleResponse<ContentRequest>>("/requests", body);
}

export function retryContentRequest(requestId: string) {
	return post<SingleResponse<ContentRequest>>(`/requests/${requestId}/retry`);
}

const PASSIVE_REQUEST_DISCOVERY_GET_OPTIONS = {
  unavailableRetries: 1,
  retryDelayMs: 250,
} as const;

export function getRequestDiscoveryRequests() {
  return get<CollectionResponse<ContentRequest>>(
    "/plugins/request_discovery/requests",
    undefined,
    PASSIVE_REQUEST_DISCOVERY_GET_OPTIONS,
  );
}

export function searchRequestDiscovery(params: { q: string; media_type: "movie" | "series"; limit?: number }) {
  return get<CollectionResponse<RequestDiscoveryResult>>("/plugins/request_discovery/search", params);
}

export function getPopularRequestDiscovery(params: { media_type: "movie" | "series"; limit?: number; page?: number }) {
  return get<CollectionResponse<RequestDiscoveryResult>>(
    "/plugins/request_discovery/popular",
    params,
    PASSIVE_REQUEST_DISCOVERY_GET_OPTIONS,
  );
}

export function getRequestDiscoverySettings() {
  return get<SingleResponse<RequestDiscoverySettings>>("/plugins/request_discovery/settings");
}

export function createRequestDiscoveryRequest(body: {
  title: string;
  media_type: "movie" | "series";
  external_id?: string;
  source?: string;
  resolution?: RequestResolution;
  critic_score?: number;
  audience_score?: number;
  note?: string;
}) {
  return post<SingleResponse<ContentRequest>>("/plugins/request_discovery/requests", body);
}

export function getRequestDiscoveryAvailability(params: { q: string; media_type: "movie" | "series" }) {
  return get<SingleResponse<RequestAvailability>>("/plugins/request_discovery/availability", params);
}

export function getDownloads() {
  return get<CollectionResponse<Download>>("/downloads");
}

export function createDownload(body: { item_id: string; device_name?: string; mode?: "original"; ttl_seconds?: number }) {
  return post<SingleResponse<Download>>("/downloads", body);
}

export function itemDownloadPath(itemId: string) {
  return `/api/v1/items/${encodeURIComponent(itemId)}/download`;
}

export function deleteDownload(downloadId: string) {
  return del(`/downloads/${downloadId}`);
}

/** Playback */

type PlaybackSessionListParams = PaginationParams & {
  include?: string;
  "filter[state]"?: PlaybackSession["attributes"]["state"];
};

export function getPlaybackSessions(params?: PlaybackSessionListParams) {
  return get<CollectionResponse<PlaybackSession>>("/playback/sessions", params);
}

export function getPlaybackResumeItems(params?: PaginationParams) {
  return get<CollectionResponse<import("@/types/api").ResumeItem>>("/playback/resume-items", params);
}

export async function getAllPlaybackResumeItems() {
  const merged: import("@/types/api").ResumeItem[] = [];
  let cursor: string | undefined;
  let pageLimit = API_MAX_PAGE_LIMIT;

  do {
    const response = await getPlaybackResumeItems({
      limit: pageLimit,
      cursor,
    });
    merged.push(...response.data);
    cursor = response.page.next_cursor ?? undefined;
    pageLimit = response.page.limit;
  } while (cursor);

  return {
    data: merged,
    page: {
      limit: pageLimit,
      next_cursor: null,
    },
    meta: {},
  } satisfies CollectionResponse<import("@/types/api").ResumeItem>;
}

function ownerScopedPlaybackSessionRequest(body: CreatePlaybackSessionRequest): CreatePlaybackSessionRequest {
  return {
    ...body,
    client_owner_id: body.client_owner_id ?? playbackSessionOwnerID(),
  };
}

const PLAYBACK_SESSION_CREATE_TIMEOUT_MS = 30_000;

export function createPlaybackSession(body: CreatePlaybackSessionRequest) {
  return post<SingleResponse<PlaybackSession>>(
    "/playback/sessions",
    ownerScopedPlaybackSessionRequest(body),
    { timeoutMs: PLAYBACK_SESSION_CREATE_TIMEOUT_MS },
  );
}

export function refreshPlaybackSession(sessionId: string, body: CreatePlaybackSessionRequest) {
  return post<SingleResponse<PlaybackSession>>(
    `/playback/sessions/${sessionId}/refresh`,
    ownerScopedPlaybackSessionRequest(body),
    { timeoutMs: PLAYBACK_SESSION_CREATE_TIMEOUT_MS },
  );
}

export function getPlaybackSession(sessionId: string, include?: string) {
  return get<SingleResponse<PlaybackSession>>(
    `/playback/sessions/${sessionId}`,
    include ? { include } : undefined,
  );
}

export function reportPlaybackEvent(sessionId: string, body: import("@/types/api").PlaybackEventRequest) {
  return post<SingleResponse<PlaybackSession>>(
    `/playback/sessions/${sessionId}/events`,
    orderedPlaybackEventBody(body),
  );
}

export function controlPlaybackSession(sessionId: string, body: import("@/types/api").PlaybackControlRequest) {
  return post<SingleResponse<PlaybackSession>>(`/playback/sessions/${sessionId}/control`, body);
}

export function reportPlaybackClientLogs(sessionId: string, body: import("@/types/api").PlaybackClientLogRequest) {
  return post<SingleResponse<PlaybackSession>>(
    `/playback/sessions/${sessionId}/client-logs`,
    orderedPlaybackClientLogBody(body),
  );
}

declare const __KNOTFIN_TELEMETRY_URL__: string;
declare const __KNOTFIN_TELEMETRY_KEY__: string;

// One-shot, user-consented playback problem report. Uploads the local client-log
// ring DIRECTLY to the CodifyWorx telemetry ingest (cross-origin, anonymous, no
// credentials) — the KnotFin server is intentionally not in this path. Only call
// after the user confirms the disclosure that detailed playback logs will be sent
// to CodifyWorx. The soft-gate key is injected at build (empty in dev/test).
export async function reportPlaybackProblem(sessionId: string, body: import("@/types/api").PlaybackClientLogRequest) {
  if (!__KNOTFIN_TELEMETRY_KEY__) {
    throw new Error("telemetry reporting is not configured for this build");
  }
  const res = await fetch(__KNOTFIN_TELEMETRY_URL__, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Telemetry-Key": __KNOTFIN_TELEMETRY_KEY__,
    },
    body: JSON.stringify({
      session_id: sessionId,
      captured_at: new Date().toISOString(),
      entries: body.entries,
    }),
  });
  if (!res.ok) {
    throw new Error(`telemetry upload failed: ${res.status}`);
  }
  return res.json().catch(() => ({}));
}

export function deletePlaybackSession(sessionId: string) {
  return del(`/playback/sessions/${sessionId}`);
}

/** Jobs */

export function getJobs(params?: PaginationParams) {
  return get<CollectionResponse<Job>>("/jobs", params);
}

export function getJobSchedules() {
  return get<CollectionResponse<import("@/types/api").JobSchedule>>("/job-schedules");
}

export function getJobSchedule(scheduleId: string) {
  return get<SingleResponse<import("@/types/api").JobSchedule>>(`/job-schedules/${scheduleId}`);
}

export function updateJobSchedule(
  scheduleId: string,
  body: { enabled?: boolean; interval?: string; pause_for?: string | null },
) {
  return patch<SingleResponse<import("@/types/api").JobSchedule>>(`/job-schedules/${scheduleId}`, body);
}

export function runJobSchedule(scheduleId: string) {
  return post<SingleResponse<Job>>(`/job-schedules/${scheduleId}/run`);
}

export function getJob(jobId: string) {
  return get<SingleResponse<Job>>(`/jobs/${jobId}`);
}

export function cancelJob(jobId: string) {
  return post<SingleResponse<Job>>(`/jobs/${jobId}/cancel`);
}

/** Admin metadata diagnostics */

export function getMetadataIssues(params?: MetadataIssueFilterParams) {
  return get<MetadataIssuesResponse>("/admin/metadata/issues", params);
}

export function getAdminPlaybackSessions(params?: {
  include?: string;
  all?: boolean;
  diagnostics?: "stream" | "source";
  limit?: number;
  cursor?: string;
}) {
  return get<PlaybackDiagnosticsResponse>("/admin/playback/sessions", params);
}

export function getAdminActivitySystem() {
  return get<SingleResponse<AdminActivitySystem>>("/admin/activity/system");
}

/** Plugins */

export function getPlugins() {
  return get<CollectionResponse<Plugin>>("/plugins");
}

export function getPluginCatalog() {
  return get<CollectionResponse<PluginCatalogEntry>>("/plugins/catalog");
}

export function installPluginFromCatalog(id: string) {
  return post<SingleResponse<Plugin>>("/plugins/catalog/install", { id });
}

export function getPluginNav() {
  return get<CollectionResponse<PluginNavEntry>>("/plugins/nav");
}

export function getPluginAdminNav() {
  return get<CollectionResponse<PluginAdminNavEntry>>("/plugins/admin-nav");
}

export function getPlugin(pluginId: string) {
  return get<SingleResponse<Plugin>>(`/plugins/${pluginId}`);
}

export function installPlugin(
  file: File,
  onUploadProgress?: (progress: FormUploadProgress) => void,
) {
  const form = new FormData();
  form.set("package", file);
  return postForm<SingleResponse<Plugin>>(
    "/plugins/install",
    form,
    onUploadProgress ? { onUploadProgress } : undefined,
  );
}

export function removePlugin(pluginId: string) {
  return del<SingleResponse<Plugin>>(`/plugins/${pluginId}`);
}

export function enablePlugin(pluginId: string) {
  return post<SingleResponse<Plugin>>(`/plugins/${pluginId}/enable`);
}

export function disablePlugin(pluginId: string) {
  return post<SingleResponse<Plugin>>(`/plugins/${pluginId}/disable`);
}
